import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import {
  Package, ChefHat, Clock, CheckCircle2, Truck, Star,
  Car, Bike, Languages, RefreshCw, MapPin, Send, MessageSquare, Phone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered'];

const STEP_ICONS = {
  pending: Clock,
  confirmed: CheckCircle2,
  preparing: ChefHat,
  ready: Package,
  out_for_delivery: Truck,
  delivered: CheckCircle2,
};

const VEHICLE_ICON = { car: Car, motorcycle: Bike, bicycle: Bike };

export default function TrackOrderPage() {
  const { orderId } = useParams();
  const { t } = useTranslation();
  const { lang, dir, toggleLanguage } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);
  const wsRef = useRef(null);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/track/${orderId}`);
      if (!res.ok) { setError(t('track.notFound')); setLoading(false); return; }
      const data = await res.json();
      setOrder(data);
      setError('');
    } catch { setError(t('track.connectionError')); }
    finally { setLoading(false); }
  }, [orderId, t]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!orderId) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/order/${orderId}`);
    wsRef.current = ws;
    const ping = setInterval(() => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' })); }, 25000);
    ws.onopen = () => setWsConnected(true);
    ws.onmessage = () => { fetchOrder(); };
    ws.onclose = () => { setWsConnected(false); setTimeout(() => {}, 3000); };
    ws.onerror = () => ws.close();
    return () => { clearInterval(ping); ws.close(); };
  }, [orderId, fetchOrder]);

  const currentStepIdx = order ? STEPS.indexOf(order.status) : -1;
  const isCancelled = order?.status === 'cancelled';
  const isDelivered = order?.status === 'delivered';
  const alreadyRated = order?.rated != null;

  const submitRating = async () => {
    if (ratingStars < 1) return;
    setRatingLoading(true);
    try {
      const res = await fetch(`${API}/api/public/track/${orderId}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stars: ratingStars, comment: ratingComment }),
      });
      if (res.ok) {
        setRatingSubmitted(true);
        fetchOrder();
      }
    } catch {}
    setRatingLoading(false);
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('track.justNow');
    if (mins < 60) return `${mins} ${t('track.minutesAgo')}`;
    const hrs = Math.floor(mins / 60);
    return `${hrs} ${t('track.hoursAgo')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400 text-sm">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" dir={dir}>
        <div className="text-center max-w-sm">
          <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">{t('track.notFound')}</h2>
          <p className="text-zinc-400 text-sm">{t('track.notFoundDesc')}</p>
        </div>
      </div>
    );
  }

  const VehicleIcon = order?.driver ? (VEHICLE_ICON[order.driver.vehicle_type] || Car) : Car;

  return (
    <div className="min-h-screen bg-zinc-950 text-white" dir={dir} data-testid="track-order-page">
      {/* Language toggle */}
      <button onClick={toggleLanguage}
        className="fixed top-4 left-4 z-50 text-xs text-zinc-400 hover:text-white bg-zinc-800/80 backdrop-blur px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5"
        data-testid="track-lang-toggle">
        <Languages className="w-3 h-3" />
        {lang === 'ar' ? 'EN' : 'عربي'}
      </button>

      {/* Connection indicator */}
      <div className={`fixed top-4 right-4 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] ${wsConnected ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}
        data-testid="track-ws-status">
        <div className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-600'}`} />
        {wsConnected ? t('track.liveUpdates') : t('track.connecting')}
      </div>

      {/* Hero header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/8 to-transparent" />
        <div className="relative px-5 pt-16 pb-8 max-w-lg mx-auto">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-3 shadow-lg shadow-amber-500/20">
              <Package className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Tajawal, Cairo, sans-serif' }} data-testid="track-title">
              {t('track.title')}
            </h1>
            {order?.branch_name && (
              <p className="text-zinc-400 text-sm mt-1">{order.branch_name}</p>
            )}
            <p className="text-zinc-500 text-xs mt-1">#{orderId?.slice(0, 8)}</p>
          </div>

          {/* Cancelled state */}
          {isCancelled && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-5 text-center mb-6" data-testid="track-cancelled">
              <div className="w-12 h-12 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-3">
                <Package className="w-6 h-6 text-red-400" />
              </div>
              <p className="font-bold text-red-400">{t('track.cancelled')}</p>
              <p className="text-zinc-500 text-xs mt-1">{t('track.cancelledDesc')}</p>
            </div>
          )}

          {/* Progress tracker */}
          {!isCancelled && (
            <div className="mb-8" data-testid="track-progress">
              <div className="flex items-center justify-between relative">
                {/* Progress line */}
                <div className="absolute top-5 left-5 right-5 h-0.5 bg-zinc-800" />
                <div className="absolute top-5 left-5 h-0.5 bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-1000"
                  style={{ width: `${Math.max(0, currentStepIdx / (STEPS.length - 1)) * (100 - 10)}%` }} />

                {STEPS.map((step, i) => {
                  const Icon = STEP_ICONS[step];
                  const isActive = i <= currentStepIdx;
                  const isCurrent = i === currentStepIdx;
                  return (
                    <div key={step} className="relative flex flex-col items-center z-10" style={{ width: `${100 / STEPS.length}%` }}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isCurrent
                          ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30 scale-110'
                          : isActive
                          ? 'bg-amber-500/20 border border-amber-500/40'
                          : 'bg-zinc-800/80 border border-zinc-700/50'
                      }`} data-testid={`track-step-${step}`}>
                        <Icon className={`w-4 h-4 ${isCurrent ? 'text-white' : isActive ? 'text-amber-400' : 'text-zinc-600'}`} />
                      </div>
                      <span className={`text-[9px] mt-2 text-center leading-tight ${isCurrent ? 'text-amber-400 font-bold' : isActive ? 'text-zinc-300' : 'text-zinc-600'}`}>
                        {t(`track.step_${step}`)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Current status message */}
          {!isCancelled && (
            <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 mb-4 backdrop-blur" data-testid="track-status-message">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  order?.status === 'delivered' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                }`}>
                  {order?.status === 'delivered'
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    : <Clock className="w-5 h-5 text-amber-400" />
                  }
                </div>
                <div>
                  <p className="font-bold text-sm">{t(`track.msg_${order?.status || 'pending'}`)}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{getTimeSince(order?.created_at)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Driver card */}
          {order?.driver && !isCancelled && (
            <div className="bg-gradient-to-br from-purple-500/5 to-amber-500/5 border border-purple-500/15 rounded-2xl p-4 mb-4" data-testid="track-driver-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-amber-500/20 flex items-center justify-center">
                  <VehicleIcon className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-zinc-500">{t('track.yourDriver')}</p>
                  <p className="font-bold text-sm">{order.driver.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Star className="w-3 h-3 text-amber-400" />
                    <span className="text-xs text-zinc-400">{order.driver.rating}</span>
                    <span className="text-zinc-700">|</span>
                    <span className="text-xs text-zinc-400">{t(`track.vehicle_${order.driver.vehicle_type}`)}</span>
                  </div>
                </div>
                {order.driver.phone && (
                  <a href={`tel:${order.driver.phone}`}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 text-emerald-400 text-xs font-semibold hover:bg-emerald-500/25 transition-colors border border-emerald-500/20"
                    data-testid="call-driver-btn">
                    <Phone className="w-4 h-4" />
                    <span>{t('track.callDriver')}</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Order items */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl overflow-hidden backdrop-blur" data-testid="track-order-items">
            <div className="px-4 py-3 border-b border-zinc-800">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-500" />
                {t('track.orderSummary')}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {order?.items?.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-amber-500 bg-amber-500/10 w-6 h-6 rounded-lg flex items-center justify-center">
                      {item.quantity}x
                    </span>
                    <span className="text-sm">{item.name_ar || item.name}</span>
                  </div>
                  <span className="text-sm text-zinc-400">{item.price * item.quantity} {t('common.sar')}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-800 p-4 space-y-2">
              <div className="flex justify-between text-xs text-zinc-500">
                <span>{t('track.subtotal')}</span>
                <span>{order?.subtotal} {t('common.sar')}</span>
              </div>
              <div className="flex justify-between text-xs text-zinc-500">
                <span>{t('track.deliveryFee')}</span>
                <span>{order?.delivery_fee} {t('common.sar')}</span>
              </div>
              <div className="flex justify-between text-sm font-bold pt-2 border-t border-zinc-800">
                <span>{t('track.total')}</span>
                <span className="text-amber-400">{order?.total_price} {t('common.sar')}</span>
              </div>
            </div>
          </div>

          {/* Delivery timeline */}
          {order?.delivery && !isCancelled && (
            <div className="mt-4 bg-zinc-900/80 border border-zinc-800 rounded-2xl p-4 backdrop-blur" data-testid="track-timeline">
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                {t('track.timeline')}
              </h3>
              <div className="space-y-3">
                {order.delivery.assigned_at && (
                  <TimelineItem label={t('track.tl_assigned')} time={order.delivery.assigned_at} />
                )}
                {order.delivery.picked_up_at && (
                  <TimelineItem label={t('track.tl_pickedUp')} time={order.delivery.picked_up_at} />
                )}
                {order.delivery.delivered_at && (
                  <TimelineItem label={t('track.tl_delivered')} time={order.delivery.delivered_at} active />
                )}
              </div>
            </div>
          )}

          {/* Rating section - shown when delivered */}
          {isDelivered && !isCancelled && (
            <div className="mt-4 bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 backdrop-blur" data-testid="track-rating-section">
              {(ratingSubmitted || alreadyRated) ? (
                <div className="text-center py-3" data-testid="track-rating-done">
                  <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-3">
                    <Star className="w-7 h-7 text-amber-400" />
                  </div>
                  <p className="font-bold text-sm text-amber-400">{t('track.thankYouRating')}</p>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-5 h-5 ${s <= (ratingStars || alreadyRated) ? 'text-amber-400 fill-amber-400' : 'text-zinc-700'}`} />
                    ))}
                  </div>
                  <p className="text-zinc-500 text-xs mt-2">{t('track.ratingRecorded')}</p>
                </div>
              ) : (
                <>
                  <h3 className="text-sm font-bold mb-1 flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    {t('track.rateTitle')}
                  </h3>
                  <p className="text-xs text-zinc-500 mb-4">{t('track.rateSubtitle')}</p>

                  {/* Stars */}
                  <div className="flex items-center justify-center gap-2 mb-4" data-testid="track-rating-stars">
                    {[1, 2, 3, 4, 5].map(s => (
                      <button key={s}
                        onClick={() => setRatingStars(s)}
                        onMouseEnter={() => setRatingHover(s)}
                        onMouseLeave={() => setRatingHover(0)}
                        className="transition-all duration-200 p-1"
                        data-testid={`star-${s}`}
                        style={{ transform: (ratingHover || ratingStars) >= s ? 'scale(1.2)' : 'scale(1)' }}>
                        <Star className={`w-8 h-8 transition-colors duration-200 ${
                          (ratingHover || ratingStars) >= s
                            ? 'text-amber-400 fill-amber-400 drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]'
                            : 'text-zinc-700'
                        }`} />
                      </button>
                    ))}
                  </div>

                  {/* Rating label */}
                  {ratingStars > 0 && (
                    <p className="text-center text-xs text-amber-400/80 mb-3" data-testid="track-rating-label">
                      {t(`track.star_${ratingStars}`)}
                    </p>
                  )}

                  {/* Comment */}
                  <div className="relative mb-4">
                    <textarea
                      value={ratingComment}
                      onChange={e => setRatingComment(e.target.value)}
                      placeholder={t('track.commentPlaceholder')}
                      rows={2}
                      maxLength={500}
                      className="w-full bg-zinc-800/80 border border-zinc-700 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none resize-none focus:border-amber-500/50 transition-colors"
                      data-testid="track-rating-comment"
                    />
                    <MessageSquare className="absolute top-3 left-3 w-4 h-4 text-zinc-600 pointer-events-none" style={{ right: 'auto', left: dir === 'rtl' ? 'auto' : '12px', [dir === 'rtl' ? 'left' : 'right']: '12px' }} />
                  </div>

                  {/* Submit */}
                  <button
                    onClick={submitRating}
                    disabled={ratingStars < 1 || ratingLoading}
                    className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all duration-300 ${
                      ratingStars > 0
                        ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40'
                        : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                    }`}
                    data-testid="track-submit-rating">
                    {ratingLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {t('track.submitRating')}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="text-center mt-6 pb-8">
            <button onClick={fetchOrder} className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1.5 mx-auto" data-testid="track-refresh">
              <RefreshCw className="w-3 h-3" />
              {t('track.refresh')}
            </button>
            <p className="text-[10px] text-zinc-700 mt-3">Zouq AI - {t('track.poweredBy')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineItem({ label, time, active }) {
  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? 'bg-emerald-400' : 'bg-amber-400'}`} />
      <span className="text-xs flex-1">{label}</span>
      <span className="text-[10px] text-zinc-500">{formatTime(time)}</span>
    </div>
  );
}
